/**
 * Mercy Oil — Spend Rewards gift engine
 * -------------------------------------------------------------
 * Automatically adds / removes the free gift products at the right
 * spend thresholds. The gifts are dedicated £0 "Complimentary" products, so
 * NO discount is involved — the gift line is inherently free. This file only
 * manages cart CONTENTS.
 *
 *   £70  — Free UK delivery (shipping rate, admin-managed)
 *   £85  — Complimentary Organic Black Seed Massage Oil 500ml (£0 variant 54882137669961)
 *
 * The "qualifying subtotal" excludes the gift lines themselves so that
 * adding a gift can never push the customer over/under a threshold and
 * cause add/remove oscillation. Any line of a gift variant that isn't earned
 * (threshold not met) is removed on every cart change (see _doReconcile).
 */
(function () {
  'use strict';

  // Dedicated £0 "Complimentary" gift products (hidden, not sold separately).
  // Because they are £0 products that no one buys normally, ANY cart line of
  // these variants is a gift (or a gaming attempt) and is managed entirely here.
  var GIFTS = [
    { key: 'massage', variantId: 54882137669961, threshold: 8500, name: 'Complimentary Organic Black Seed Massage Oil 500ml' }
  ];
  var GIFT_VARIANT_IDS = GIFTS.map(function (g) { return g.variantId; });

  /* --- currency helpers (parity with the theme's progress bar) --- */
  function rate() {
    return (window.Shopify && Shopify.currency && Shopify.currency.rate)
      ? parseFloat(Shopify.currency.rate) || 1
      : 1;
  }
  function threshold(baseCents) {
    return Math.round(baseCents * rate());
  }

  /* --- gift-line detection ---
     A line is a gift if it carries the _free_gift property OR is one of the
     dedicated £0 gift variants. (Hidden-line rendering in Liquid still keys off
     _free_gift only, as required; this is just for the engine's own bookkeeping.) */
  function isGiftLine(item) {
    if (!item) return false;
    if (item.properties && item.properties._free_gift) return true;
    return GIFT_VARIANT_IDS.indexOf(item.variant_id) !== -1;
  }

  /* --- qualifying subtotal = non-gift lines' final_line_price --- */
  function computeQualifyingSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce(function (sum, item) {
      return isGiftLine(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  /* --- per-session dismissal (customer manually removed a gift) --- */
  function dismissKey(variantId) { return 'mercy_gift_dismissed_' + variantId; }
  function isDismissed(variantId) {
    try { return sessionStorage.getItem(dismissKey(variantId)) === '1'; }
    catch (e) { return false; }
  }
  function setDismissed(variantId, val) {
    try {
      if (val) { sessionStorage.setItem(dismissKey(variantId), '1'); }
      else { sessionStorage.removeItem(dismissKey(variantId)); }
    } catch (e) { /* private mode — ignore */ }
  }

  /* --- reconcile engine: serialised, one mutation batch in flight --- */
  var Engine = {
    running: false,
    rerun: false,
    lastPresent: {},   // gift key -> was-present at previous reconcile
    dedupTried: {},    // dup signature -> already attempted this burst (convergence guard)

    reconcile: function () {
      var self = this;
      if (self.running) { self.rerun = true; return; }
      self.running = true;
      (function loop() {
        self.rerun = false;
        return self._doReconcile().then(function () {
          if (self.rerun) { return loop(); }
        });
      })().catch(function (e) {
        console.warn('[MercyRewards] reconcile failed', e);
      }).then(function () {
        self.running = false;
      });
    },

    _doReconcile: function () {
      var self = this;
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var subtotal = computeQualifyingSubtotal(cart);

          var actions = [];

          GIFTS.forEach(function (g) {
            var qualifies = subtotal >= threshold(g.threshold);

            // Inspect EVERY line of this dedicated £0 gift variant. The engine adds
            // the gift with a _free_gift property (and that line is hidden by the
            // Liquid). Anyone gaming it can add the £0 product straight from its URL
            // with NO property — so we key off the VARIANT, not the property, and
            // treat any non-engine line as a "stray" to be removed. Line keys are
            // exact and position-independent.
            var giftLine = null;   // the engine's own hidden _free_gift line
            var strayKeys = [];    // any other line of this gift variant (e.g. URL-added)
            cart.items.forEach(function (item) {
              if (item.variant_id !== g.variantId) { return; }
              if (item.properties && item.properties._free_gift && !giftLine) {
                giftLine = item;
              } else {
                strayKeys.push(item.key);
              }
            });
            var present = !!giftLine;
            var wasPresent = !!self.lastPresent[g.key];

            // Customer manually removed the gift while still qualifying →
            // remember it so we don't force it back in this session.
            if (qualifies && !present && wasPresent && !isDismissed(g.variantId)) {
              setDismissed(g.variantId, true);
            }
            // Dropped below the threshold → clear dismissal so that
            // re-crossing it later re-adds the gift.
            if (!qualifies && isDismissed(g.variantId)) {
              setDismissed(g.variantId, false);
            }

            // Strays are never legitimate — strip them whether qualifying or not.
            strayKeys.forEach(function (k) { actions.push({ type: 'remove', key: k }); });

            if (!qualifies) {
              // ENFORCEMENT: threshold not met → the gift must not be in the cart,
              // including one someone added by URL to try to grab the free product.
              if (present) { actions.push({ type: 'remove', key: giftLine.key }); }
            } else if (!present && !isDismissed(g.variantId)) {
              actions.push({ type: 'add', gift: g });
            } else if (present && giftLine.quantity !== 1) {
              actions.push({ type: 'setqty', key: giftLine.key }); // never more than 1
            }

            self.lastPresent[g.key] = present;
          });

          // Transition cleanup: remove any _free_gift line that is NOT one of the
          // current gift variants — e.g. an old real-product gift left in an
          // in-flight cart by the previous engine. It carries _free_gift, so it was
          // engine-added (never a paid line), and is safe to strip so a customer is
          // never left holding both the old and the new gift.
          cart.items.forEach(function (item) {
            if (item.properties && item.properties._free_gift &&
                GIFT_VARIANT_IDS.indexOf(item.variant_id) === -1) {
              actions.push({ type: 'remove', key: item.key });
            }
          });

          // NOTE: client-side duplicate-line MERGING has been removed. The
          // positional /cart/update it used raced with in-flight adds and
          // corrupted quantities (e.g. 8+2 -> 6+2, losing units). Duplicates are
          // prevented upstream by the serialised cart-write queue; this engine no
          // longer mutates paid line quantities at all. If a duplicate ever slips
          // through it now just sits there harmlessly — never corrupts or loops —
          // and we fix the split at its source instead of healing from here.
          if (!actions.length) { return false; }

          // Gift lines are hidden + free; the visible UI is server-rendered from
          // the qualifying subtotal, so gift add/remove needs no re-render.
          return self._runActions(actions).then(function (changed) { return changed; });
        });
    },

    _runActions: function (actions) {
      var changed = false;
      var chain = Promise.resolve();
      actions.forEach(function (a) {
        chain = chain.then(function () {
          if (a.type === 'add') {
            return fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                items: [{ id: a.gift.variantId, quantity: 1, properties: { _free_gift: 'true' } }]
              })
            }).then(function (r) {
              // 422 = unavailable / out of stock → fail silently, never break checkout.
              if (r.ok) { changed = true; }
            }).catch(function () { /* silent */ });
          }
          if (a.type === 'remove' || a.type === 'setqty' || a.type === 'setqtyKey') {
            var qty = a.type === 'remove' ? 0 : (a.type === 'setqtyKey' ? a.quantity : 1);
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              // Target by line KEY (id), never index — robust to line reordering.
              body: JSON.stringify({ id: a.key, quantity: qty })
            }).then(function (r) {
              if (r.ok) { changed = true; }
            }).catch(function () { /* silent */ });
          }
        });
      });
      return chain.then(function () { return changed; });
    },

    _refresh: function () {
      if (typeof window.refreshCart === 'function') {
        return Promise.resolve(window.refreshCart()).catch(function () {});
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return Promise.resolve();
    }
  };

  /* --- explicit gift removal (from the progress-bar popover) --- */
  function removeGift(key) {
    var gift = GIFTS.filter(function (g) { return g.key === key; })[0];
    if (!gift) return Promise.resolve();
    // Remember the dismissal so we don't force it back this session.
    setDismissed(gift.variantId, true);
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var lineKey = null;
        cart.items.forEach(function (it) {
          if (it.variant_id === gift.variantId && it.properties && it.properties._free_gift) { lineKey = it.key; }
        });
        if (!lineKey) return null;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        }).then(function () {
          if (typeof window.refreshCart === 'function') { return window.refreshCart(); }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        });
      })
      .catch(function () { /* silent */ });
  }

  /* --- shared surface for the progress bar and other scripts --- */
  window.MercyRewards = {
    gifts: GIFTS,
    giftVariantIds: GIFT_VARIANT_IDS,
    isGiftLine: isGiftLine,
    computeQualifyingSubtotal: computeQualifyingSubtotal,
    threshold: threshold,
    reconcile: function () { Engine.reconcile(); },
    removeGift: removeGift
  };

  /* --- react to every cart-change signal the theme emits --- */
  var trigger = function () { Engine.reconcile(); };
  document.addEventListener('cart:refreshed', trigger);
  document.addEventListener('line-item:change:end', trigger);
  document.addEventListener('cart:item-added', trigger);

  // Initial pass — covers carts that already qualify on page load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trigger);
  } else {
    trigger();
  }
})();

/* ------------------------------------------------------------------
 * Smooth the progress-bar fill across drawer re-renders. The bar is
 * server-rendered (plain HTML), so after a re-render the fill sits at
 * its final width with no animation. This eases it from the previous
 * value to the new one. Plain DOM only — no custom elements.
 * ------------------------------------------------------------------ */
(function () {
  var lastFill = null;
  function animateFill() {
    var fill = document.querySelector('.upb-fill');
    if (!fill) return;
    var target = fill.style.width;               // final width from the fresh server render
    if (lastFill === null || lastFill === target) { lastFill = target; return; }
    fill.style.width = lastFill;                  // snap back to previous BEFORE paint (no flash)
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { fill.style.width = target; }); // then ease to new width
    });
    lastFill = target;
  }
  ['cart:refreshed', 'line-item:change:end', 'cart:item-added'].forEach(function (ev) {
    document.addEventListener(ev, animateFill);
  });
  var init = function () { var f = document.querySelector('.upb-fill'); if (f) lastFill = f.style.width; };
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
})();
